import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { DialogGeometry, DialogSnapZone } from './is-dialog-geometry';
import { ISDialogLayout, workspaceRects } from './is-dialog-workspace-layout';

/** Apenas metadados genéricos. Os módulos do CRM não precisam de alterações. */
export interface ISDialogTaskbarItem {
  id: string;
  title: string;
  icon?: string;
  minimized: boolean;
  inPictureInPicture: boolean;
  pinned?: boolean;
  active?: boolean;
  maximized?: boolean;
  snapped?: boolean;
  restore: () => void;
  close: () => void;
  minimize: () => void;
  focus?: () => void;
  arrange?: (geometry: DialogGeometry) => void;
  snap?: (zone: DialogSnapZone) => void;
  center?: () => void;
  resetSize?: () => void;
}

export interface ISDialogPreferences {
  animations: boolean;
  dragSnap: boolean;
}

export type ISDialogWindowPatch = Partial<Pick<ISDialogTaskbarItem,
  'title' | 'minimized' | 'inPictureInPicture' | 'pinned' | 'active' | 'maximized' | 'snapped'>>;

@Injectable({ providedIn: 'root' })
export class ISDialogStyleHandlerService {
  /** Conservados por compatibilidade com chamadas já existentes. */
  dialogIds: string[] = [];
  minimizedDialogs: string[] = [];

  private readonly windows = new Map<string, ISDialogTaskbarItem>();
  private readonly windowsSubject = new BehaviorSubject<ISDialogTaskbarItem[]>([]);
  readonly windows$ = this.windowsSubject.asObservable();
  private activeId: string | null = null;
  private readonly preferencesKey = 'isdialog:preferences:v3';
  readonly preferences: ISDialogPreferences = this.loadPreferences();
  private lastLayout: Array<{ id: string; geometry: DialogGeometry }> = [];

  get items(): ISDialogTaskbarItem[] { return Array.from(this.windows.values()); }
  getWindow(id: string): ISDialogTaskbarItem | undefined { return this.windows.get(id); }

  setPreference<K extends keyof ISDialogPreferences>(key: K, value: ISDialogPreferences[K]): void {
    this.preferences[key] = value;
    try { localStorage.setItem(this.preferencesKey, JSON.stringify(this.preferences)); } catch { /* opcional */ }
  }

  private loadPreferences(): ISDialogPreferences {
    const defaults: ISDialogPreferences = { animations: true, dragSnap: true };
    try {
      const saved = JSON.parse(localStorage.getItem(this.preferencesKey) || 'null');
      if (saved && typeof saved === 'object') {
        return {
          animations: typeof saved.animations === 'boolean' ? saved.animations : defaults.animations,
          dragSnap: typeof saved.dragSnap === 'boolean' ? saved.dragSnap : defaults.dragSnap
        };
      }
    } catch { /* opcional */ }
    return defaults;
  }
  get activeWindow(): ISDialogTaskbarItem | undefined { return this.activeId ? this.windows.get(this.activeId) : undefined; }

  addDialog(id: string): void {
    if (!this.dialogIds.includes(id)) this.dialogIds.push(id);
  }

  registerWindow(item: ISDialogTaskbarItem): void {
    this.windows.set(item.id, item);
    this.focusWindow(item.id);

    // ISDialogComponent regista-se em ngOnInit, mas o elemento .dialog pode
    // ainda não ter sido inserido no document.body pelo ISDialogService.
    // A sincronização após a montagem garante z-index à primeira abertura,
    // sem depender de o utilizador clicar novamente na janela.
    Promise.resolve().then(() => {
      if (this.windows.has(item.id)) this.updateFocusAndZIndex();
    });
  }

  patchWindow(id: string, patch: ISDialogWindowPatch): void {
    const previous = this.windows.get(id);
    if (!previous) return;
    if (Object.keys(patch).every(key => (previous as any)[key] === (patch as any)[key])) return;
    this.windows.set(id, { ...previous, ...patch });
    if (patch.minimized === true && this.activeId === id) {
      this.activeId = this.findTopVisibleId(id);
    }
    this.updateFocusAndZIndex();
  }

  removeDialog(id: string): void {
    this.dialogIds = this.dialogIds.filter(value => value !== id);
    this.windows.delete(id);
    if (this.activeId === id) this.activeId = this.findTopVisibleId();
    this.updateFocusAndZIndex();
  }

  minimizeDialog(tabId: string): void {
    if (!this.minimizedDialogs.includes(tabId)) this.minimizedDialogs.push(tabId);
  }

  removeMinimizeDialog(tabId: string): void {
    this.minimizedDialogs = this.minimizedDialogs.filter(id => id !== tabId);
  }

  minimizeAllDialogs(): void {
    this.items.forEach(item => {
      if (!item.minimized && !item.inPictureInPicture) item.minimize();
    });
  }

  restoreAllDialogs(): void {
    this.items.forEach(item => { if (item.minimized) item.restore(); });
  }

  focusWindow(id: string): void {
    const item = this.windows.get(id);
    if (!item) return;
    if (item.minimized) { item.restore(); return; }
    if (item.inPictureInPicture) { item.focus?.(); return; }
    this.activeId = id;
    this.dialogIds = this.dialogIds.filter(value => value !== id).concat(id);
    this.updateFocusAndZIndex();
  }

  focusNextWindow(): void {
    const candidates = this.items.filter(item => !item.inPictureInPicture);
    if (!candidates.length) return;
    const index = candidates.findIndex(item => item.id === this.activeId);
    this.focusWindow(candidates[(index + 1) % candidates.length].id);
  }

  togglePin(id: string): void {
    const item = this.windows.get(id);
    if (!item) return;
    this.patchWindow(id, { pinned: !item.pinned });
    this.focusWindow(id);
  }

  onMouseClick(target: EventTarget | null): boolean {
    if (!this.dialogIds.length) return false;
    return !this.dialogIds.some(id => document.getElementById(id)?.contains(target as Node));
  }

  updateZindex(id: string): void { this.focusWindow(id); }

  /** Nunca altera janelas em PiP/minimizadas nem força fecho de formulários. */
  arrangeWindows(layout: ISDialogLayout): number {
    const visible = this.items.filter(item => !item.minimized && !item.inPictureInPicture && !!item.arrange);
    if (!visible.length) return 0;
    this.lastLayout = visible.map(item => {
      const el = document.getElementById(item.id);
      const r = el?.getBoundingClientRect();
      return { id: item.id, geometry: {
        x: r?.left ?? 0, y: r?.top ?? 0, width: r?.width ?? 700, height: r?.height ?? 420
      }};
    });
    const rects = workspaceRects(visible.length, { width: window.innerWidth, height: window.innerHeight }, layout);
    visible.forEach((item, index) => item.arrange?.(rects[index]));
    this.focusWindow(visible[visible.length - 1].id);
    return visible.length;
  }

  undoArrange(): void {
    const previous = this.lastLayout;
    this.lastLayout = [];
    previous.forEach(entry => this.windows.get(entry.id)?.arrange?.(entry.geometry));
  }

  private findTopVisibleId(exclude?: string): string | null {
    for (const id of this.dialogIds.slice().reverse()) {
      const w = this.windows.get(id);
      if (id !== exclude && w && !w.minimized && !w.inPictureInPicture) return id;
    }
    return null;
  }

  private updateFocusAndZIndex(): void {
    const ordered = this.dialogIds.filter(id => this.windows.has(id));
    const normal = ordered.filter(id => !this.windows.get(id)?.pinned);
    const pinned = ordered.filter(id => this.windows.get(id)?.pinned);
    normal.concat(pinned).forEach((id, index) => {
      const element = document.getElementById(id);
      if (element) element.style.zIndex = String(15000 + index);
    });
    const previous = this.windowsSubject.value;
    const next = this.items.map(item => ({ ...item, active: item.id === this.activeId }));
    this.windows.clear();
    next.forEach(item => this.windows.set(item.id, item));
    if (next.length !== previous.length || next.some((item, i) =>
      previous[i]?.id !== item.id || previous[i]?.title !== item.title ||
      previous[i]?.active !== item.active || previous[i]?.minimized !== item.minimized ||
      previous[i]?.pinned !== item.pinned || previous[i]?.inPictureInPicture !== item.inPictureInPicture ||
      previous[i]?.maximized !== item.maximized || previous[i]?.snapped !== item.snapped
    )) this.windowsSubject.next(next);
  }
}
